/**
 * Mark-event resolution (S11) — turns a normalized Recharts click into the
 * stable `ChartMarkEvent` contract by resolving datum keys back through the
 * ChartConfig mappings. Pure; the click-payload normalization (Recharts arg
 * shapes vary per element) lives in `normalizeRechartsClick` below so every
 * chart view shares one defensive adapter.
 */
import type {
  ChartConfig,
  ChartMarkEvent,
  RawMarkClick,
  TransformedChartData,
} from '../types/chartTypes';
import { CHART_RAW_X_KEY } from '../types/chartTypes';

/**
 * Decode which series VALUE a clicked series key represents.
 * - single Y + series: the key IS the series value (`point[seriesValue]`)
 * - multi Y + series: compound `"{yLabel} ({seriesValue})"` keys
 * - no series mapping: keys are Y columns — not a categorical filter target
 */
export function decodeSeriesValue(config: ChartConfig, seriesKey: string): string | undefined {
  if (!config.series?.column) return undefined;
  if (config.yAxis.length <= 1) return seriesKey;
  for (const yCol of config.yAxis) {
    const prefix = `${yCol.label || yCol.column} (`;
    if (seriesKey.startsWith(prefix) && seriesKey.endsWith(')')) {
      return seriesKey.slice(prefix.length, -1);
    }
  }
  return undefined;
}

export function resolveMarkEvent(
  config: ChartConfig,
  data: TransformedChartData,
  click: RawMarkClick,
): ChartMarkEvent | null {
  if (!click.datum || !config.xAxis?.column) return null;
  // Pie datums key the category as 'name' regardless of config labels.
  const xKey = config.type === 'pie' ? 'name' : data.xDataKey;
  // Prefer the machine value over the locale-formatted label (C3): for date
  // axes the label is unparseable outside en-US and loses granularity.
  const raw = CHART_RAW_X_KEY in click.datum ? click.datum[CHART_RAW_X_KEY] : click.datum[xKey];
  if (raw === undefined) return null;
  const value = raw === null || typeof raw === 'number' ? raw : String(raw);

  const seriesValue = click.seriesKey ? decodeSeriesValue(config, click.seriesKey) : undefined;
  return {
    field: config.xAxis.column,
    value,
    ...(seriesValue !== undefined && config.series?.column
      ? { seriesField: config.series.column, seriesValue }
      : {}),
  };
}

/**
 * Recharts hands click handlers different argument shapes per element (Bar
 * sector props, Pie sector props with a nested payload, activeDot props).
 * Scan the args for the first plain object carrying a `payload` and unwrap
 * one nesting level (pie sectors nest datum at `.payload.payload`).
 */
export function normalizeRechartsClick(...args: unknown[]): RawMarkClick {
  for (const arg of args) {
    if (!arg || typeof arg !== 'object' || arg instanceof Event) continue;
    const candidate = arg as Record<string, unknown>;
    if (!('payload' in candidate)) continue;
    let datum = candidate.payload as Record<string, unknown> | null;
    if (datum && typeof datum === 'object' && 'payload' in datum && typeof datum.payload === 'object') {
      datum = datum.payload as Record<string, unknown>;
    }
    const seriesKey =
      typeof candidate.dataKey === 'string'
        ? candidate.dataKey
        : typeof candidate.name === 'string'
          ? candidate.name
          : undefined;
    return { datum: datum ?? null, seriesKey };
  }
  return { datum: null };
}
